import { Accept, useDropzone } from "react-dropzone";
import IImageFieldProps from "./IImageFieldProps";
import styles from "./ImageField.module.scss";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button, mergeClasses, Tooltip } from "@fluentui/react-components";
import { mergeStyles } from "@fluentui/merge-styles";
import { useTranslation } from "react-i18next";
import { Prohibited48Filled } from "@fluentui/react-icons";

const acceptedFileTypes: Accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/webp': ['.webp'],
    'image/svg+xml': ['.svg']
};

export default function ImageField(props: IImageFieldProps) {
    const { t } = useTranslation();

    const [imageHeight, setImageHeight] = useState<number>(0);
    
    const dropzoneRef = useRef<HTMLDivElement>(null);

    const {getRootProps, getInputProps} = useDropzone({
        accept: acceptedFileTypes,
        multiple: false,
        onDrop: (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                URL.revokeObjectURL(backgroundImage);
                props.onImageChange(acceptedFiles[0]);
            }
        }
    });

    const updateImageHeight = useCallback(() => {
        if (!props.image || !dropzoneRef.current) {
            setImageHeight(0);
            return;
        }

        const img = new Image();
        img.src = URL.createObjectURL(props.image);
        img.onload = () => {
            const aspectRatio = img.height / img.width;
            const dropzoneWidth = dropzoneRef.current!.offsetWidth;
            setImageHeight(dropzoneWidth * aspectRatio);
        };
    }, [props.image]);

    useEffect(updateImageHeight, [props.image, updateImageHeight]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(updateImageHeight);
        const currentDropzoneRef = dropzoneRef.current;
        if (currentDropzoneRef) {
            resizeObserver.observe(currentDropzoneRef);
        }

        return () => {
            if (currentDropzoneRef) {
                resizeObserver.unobserve(currentDropzoneRef);
            }
        };
    }, [props.image, updateImageHeight]);

    const handleResetClicked = (e: React.MouseEvent) => {
        e.stopPropagation();
        props.onImageChange(null!);
    }

    const backgroundImage = props.image 
        ? URL.createObjectURL(props.image) 
        : '';

    const backgroundStyle = mergeStyles({ 
        backgroundImage: `url(${backgroundImage})`,
        height: imageHeight ? `${imageHeight}px` : 'auto'
    });

    const dropzoneStyle = mergeClasses(styles.dropzone, backgroundStyle);

    return (
        <section>
            <div {...getRootProps({className: dropzoneStyle})} ref={dropzoneRef}>
                <input {...getInputProps()} />
                <p className={props.image ? styles.imageSelected : null!}>
                    {
                        !props.image
                            ? t("dropzone.dropImage")
                            : t("dropzone.dropNewImage")
                    }
                </p>
                {
                    props.image &&
                    <Tooltip content={t("dropzone.reset")} relationship='label'>
                        <Button 
                            className={styles.removeButton} 
                            icon={<Prohibited48Filled />} 
                            onClick={handleResetClicked} 
                            disabled={!props.image} 
                            />
                    </Tooltip>
                }
            </div>
        </section>
    );
}